import { av1SequenceConfig } from './av1-config';
import { avcCodecStringFromAvcC, selectVideoCodec, type HardwareAcceleration, type VideoCodecRequest } from './codec-select';

export interface PublicationPreflightOptions {
  codec: 'auto' | 'h264' | 'av1';
  width: number; height: number; fps: number; videoKbps: number; audioKbps: number;
  sampleRate?: number; numberOfChannels?: number; signal?: AbortSignal;
  /** A separate reader samples the program track; it never stops the publisher's source. */
  stream?: MediaStream;
  /**
   * Also prove an H.264 `compat` lane at this size when the publication is AV1: a real encode of full-size program
   * pictures (the encoder scales), preferring hardware, keeping up in real time. Its failure is not fatal.
   */
  fallback?: { width: number; height: number; fps: number; videoKbps: number };
}
export interface FallbackPreflight {
  codecs: string;
  videoConfig: VideoEncoderConfig;
}
export interface PublicationPreflight {
  codec: 'h264' | 'av1';
  /** Auto only: why an earlier candidate (AV1) was not used, for the publisher's log. */
  fallbackReason?: string;
  codecs: string;
  videoConfig: VideoEncoderConfig;
  /**
   * Only when `fallback` was asked and the publication is AV1: the compat lane's proven encoder, or null when no
   * H.264 encoder passed (the broadcast then goes AV1-only; `fallbackError` says why).
   */
  fallback?: FallbackPreflight | null;
  fallbackError?: string;
}
type AttemptOptions = PublicationPreflightOptions & {
  codec: 'h264' | 'av1';
  /**
   * Auto only: AV1 must also keep up with the program in real time, or Auto publishes H.264. The capability
   * proof alone accepts a software AV1 encoder that falls behind at the program's size and rate.
   */
  realtime?: boolean;
  /** First encoder preference of the attempt (default no-preference, which falls back to software on a stall). */
  hardwareAcceleration?: HardwareAcceleration;
  /**
   * Compat probe: the program pictures are this size (main's) while the encoder is configured at width × height;
   * the encoder must scale them, and report the configured size.
   */
  sourceSize?: { width: number; height: number };
};
type Attempt = (options: AttemptOptions) => Promise<PublicationPreflight>;

/** Frames of the real-time check: one second of program, at least 5. */
export function realtimeFrames(fps: number): number { return Math.max(5, Math.round(fps)); }

/** Deadline of one candidate: the capability proof, plus the real-time second and its flush when asked. */
export function attemptDeadlineMs(fps: number, realtime: boolean): number {
  return realtime ? 3000 + Math.ceil(realtimeFrames(fps) * 1000 / Math.max(1, fps)) + 500 : 3000;
}

/**
 * Whether an encoder kept up. Paced (live program frames): it never held more than 3 frames and finished within
 * 3 frame intervals of the last input. Unpaced (synthetic frames back to back): it encoded at least 1.25 times
 * the program rate.
 */
export function keptUp(r: { fps: number; frames: number; outputs: number; maxQueue: number; tailMs: number; elapsedMs: number; paced: boolean }): boolean {
  if (r.outputs < r.frames) return false;
  const interval = 1000 / r.fps;
  return r.paced ? r.maxQueue <= 3 && r.tailMs <= 3 * interval : r.elapsedMs <= (r.frames * interval) / 1.25;
}

/** No network/publication side effects. Auto fallback finishes before go-live obtains a generation. */
export async function preflightPublication(options: PublicationPreflightOptions, attempt: Attempt = encodeProbe): Promise<PublicationPreflight> {
  const candidates: Array<'h264'|'av1'> = options.codec === 'auto' ? ['av1','h264'] : [options.codec];
  let failure: unknown;
  for (const codec of candidates) {
    options.signal?.throwIfAborted();
    const lifetime = new AbortController();
    const cancel = () => lifetime.abort(options.signal?.reason);
    let rejectAbort!: (reason: unknown) => void;
    const aborted = new Promise<never>((_, reject) => { rejectAbort = reject; });
    const onAbort = () => rejectAbort(lifetime.signal.reason);
    lifetime.signal.addEventListener('abort', onAbort, {once:true});
    options.signal?.addEventListener('abort', cancel, {once:true});
    const realtime = options.codec === 'auto' && codec === 'av1';
    const timer = setTimeout(() => lifetime.abort(new Error('encoder preflight deadline')), attemptDeadlineMs(options.fps, realtime));
    let result: PublicationPreflight;
    try {
      result = await Promise.race([attempt({...options,codec,signal:lifetime.signal,realtime}), aborted]);
      if (failure !== undefined) result = {...result, fallbackReason: `${candidates[0]!.toUpperCase()}: ${failure instanceof Error ? failure.message : String(failure)}`};
    }
    catch (error) { failure = error; options.signal?.throwIfAborted(); continue; }
    finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', cancel);
      lifetime.signal.removeEventListener('abort', onAbort);
    }
    if (!options.fallback || result.codec !== 'av1') return result;
    return { ...result, ...(await preflightFallback(options, options.fallback, attempt)) };
  }
  throw new Error(`${options.codec.toUpperCase()} publication preflight failed: ${failure instanceof Error ? failure.message : String(failure)}`);
}

/**
 * The compat lane's H.264 encoder: hardware first, then whatever the browser has, each with its own deadline. Only
 * a cancellation propagates; any other failure means no compat lane.
 */
async function preflightFallback(options: PublicationPreflightOptions, fallback: NonNullable<PublicationPreflightOptions['fallback']>,
  attempt: Attempt): Promise<Pick<PublicationPreflight, 'fallback' | 'fallbackError'>> {
  const errors: string[] = [];
  for (const hardwareAcceleration of ['prefer-hardware', 'no-preference'] as const) {
    options.signal?.throwIfAborted();
    const lifetime = new AbortController();
    const cancel = () => lifetime.abort(options.signal?.reason);
    let rejectAbort!: (reason: unknown) => void;
    const aborted = new Promise<never>((_, reject) => { rejectAbort = reject; });
    const onAbort = () => rejectAbort(lifetime.signal.reason);
    lifetime.signal.addEventListener('abort', onAbort, {once:true});
    options.signal?.addEventListener('abort', cancel, {once:true});
    const timer = setTimeout(() => lifetime.abort(new Error('compat encoder preflight deadline')), attemptDeadlineMs(fallback.fps, true));
    try {
      const result = await Promise.race([attempt({ ...options, ...fallback, codec: 'h264', signal: lifetime.signal, realtime: true,
        hardwareAcceleration, sourceSize: { width: options.width, height: options.height } }), aborted]);
      return { fallback: { codecs: result.codecs, videoConfig: result.videoConfig } };
    } catch (error) {
      options.signal?.throwIfAborted();
      errors.push(`${hardwareAcceleration}: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', cancel);
      lifetime.signal.removeEventListener('abort', onAbort);
    }
  }
  return { fallback: null, fallbackError: errors.join('; ') };
}

/** Encode actual pictures and AAC samples; isConfigSupported alone is insufficient. Exported for tests. */
export async function encodeProbe(options: AttemptOptions): Promise<PublicationPreflight> {
  if (options.hardwareAcceleration && options.hardwareAcceleration !== 'no-preference') return encodeProbeAttempt(options, options.hardwareAcceleration);
  try { return await encodeProbeAttempt(options, 'no-preference'); }
  catch (error) {
    options.signal?.throwIfAborted();
    if (!(error instanceof StalledVideoEncoder)) throw error;
    // The outer attempt still owns the original 3s deadline. Re-read fresh
    // program pictures and return the exact software config that encoded them.
    return encodeProbeAttempt(options, 'prefer-software');
  }
}

class StalledVideoEncoder extends Error {}

async function encodeProbeAttempt(options: AttemptOptions, hardwareAcceleration: HardwareAcceleration): Promise<PublicationPreflight> {
  if (typeof VideoEncoder !== 'function' || typeof AudioEncoder !== 'function') throw new Error('WebCodecs video and AAC encoders are required');
  let video: VideoEncoder | undefined, audio: AudioEncoder | undefined;
  let reader: ReadableStreamDefaultReader<VideoFrame> | undefined, source: VideoFrame | undefined;
  let stallTimer: ReturnType<typeof setTimeout> | undefined;
  let pictures = 0, samples = 0, actualCodec: string | null = null, lastOutputAt = 0;
  let rejectFailure!: (error: unknown) => void;
  const failed = new Promise<never>((_,reject)=>{rejectFailure=reject;});
  let failedSettled = false;
  failed.catch(() => { failedSettled = true; });
  const onAbort = () => rejectFailure(options.signal?.reason ?? new Error('preflight canceled'));
  options.signal?.addEventListener('abort',onAbort,{once:true});
  try {
    options.signal?.throwIfAborted();
    const req: VideoCodecRequest = {...options,hardwareAcceleration};
    const selected = await Promise.race([selectVideoCodec(req, config => {
      options.signal?.throwIfAborted();
      return VideoEncoder.isConfigSupported(config);
    }), failed]);
    options.signal?.throwIfAborted();
    if (!selected) throw new Error('video configuration is unsupported');
    const audioConfig: AudioEncoderConfig = { codec:'mp4a.40.2',sampleRate:options.sampleRate ?? 48000,
      numberOfChannels:options.numberOfChannels ?? 2,bitrate:options.audioKbps*1000 };
    if (!(await Promise.race([AudioEncoder.isConfigSupported(audioConfig), failed])).supported) throw new Error('AAC configuration is unsupported');
    options.signal?.throwIfAborted();
    if (options.stream) {
      const track=options.stream.getVideoTracks()[0];
      if(!track || typeof MediaStreamTrackProcessor!=='function')throw new Error('Program video track and processor are required');
      reader=new MediaStreamTrackProcessor({track:track as MediaStreamVideoTrack}).readable.getReader();
    }
    video = new VideoEncoder({error:error=>rejectFailure(new Error(`Video encoder: ${error.message}`)),output:(chunk,meta)=>{
      try {
        pictures++;
        clearTimeout(stallTimer);
        lastOutputAt = performance.now();
        if (chunk.type !== 'key') return;
        if (options.codec === 'av1') {
          const bytes = new Uint8Array(chunk.byteLength); chunk.copyTo(bytes);
          actualCodec = av1SequenceConfig(bytes)?.codec ?? null;
        } else if (meta?.decoderConfig?.description) {
          const coded=meta.decoderConfig;
          if(options.sourceSize && coded.codedWidth && coded.codedHeight && (coded.codedWidth!==options.width || coded.codedHeight!==options.height)){
            throw new Error(`encoder did not scale ${options.sourceSize.width}x${options.sourceSize.height} to ${options.width}x${options.height} (got ${coded.codedWidth}x${coded.codedHeight})`);
          }
          const d=meta.decoderConfig.description;
          actualCodec=avcCodecStringFromAvcC(ArrayBuffer.isView(d)?new Uint8Array(d.buffer,d.byteOffset,d.byteLength):new Uint8Array(d));
        }
      } catch(error) { rejectFailure(error); }
    }});
    audio = new AudioEncoder({error:error=>rejectFailure(new Error(`AAC encoder: ${error.message}`)),output:()=>{samples++;}});
    video.configure(selected.config); audio.configure(audioConfig);
    // Three fresh pictures also exercise capture's initial pixel/color transition.
    // Cloning its first frame three times can declare a transient startup header.
    // This remains capability proof, not a throughput/quality gate.
    const picture = options.sourceSize ?? { width: options.width, height: options.height };
    const pixels = new Uint8Array(reader ? 0 : picture.width*picture.height*4);
    for(let i=3;i<pixels.length;i+=4)pixels[i]=255;
    for(let i=0;i<3;i++){
      if(reader){
        const reading=reader.read().then(frame=>{
          if(options.signal?.aborted){frame.value?.close();options.signal.throwIfAborted();}
          return frame;
        });
        const captured=await Promise.race([reading,failed]);
        if(captured.done||!captured.value)throw new Error('Program video track ended before preflight');
        source=captured.value;
      }
      const timestamp=Math.round(i*1e6/options.fps);
      const frame=source ? new VideoFrame(source,{timestamp}) :
        new VideoFrame(pixels,{format:'RGBA',codedWidth:picture.width,codedHeight:picture.height,timestamp});
      try { video.encode(frame,{keyFrame:i===0}); } finally { frame.close();source?.close();source=undefined; }
    }
    // Mirrors the live encoder watchdog for the observed zero-output H264
    // hardware stall. Arm only after all three inputs, never during capture or
    // capability lookup. A single output keeps the normal deadline authoritative.
    if (options.codec === 'h264' && hardwareAcceleration === 'no-preference' && pictures === 0) {
      stallTimer = setTimeout(() => rejectFailure(new StalledVideoEncoder('H264 encoder emitted no pictures')), 1500);
    }
    const data=new AudioData({format:'f32-planar',sampleRate:audioConfig.sampleRate,numberOfFrames:2048,
      numberOfChannels:audioConfig.numberOfChannels,timestamp:0,data:new Float32Array(2048*audioConfig.numberOfChannels)});
    try { audio.encode(data); } finally { data.close(); }
    await Promise.race([Promise.all([video.flush(),audio.flush()]),failed]);
    options.signal?.throwIfAborted();
    if(pictures!==3 || !samples || !actualCodec)throw new Error('encoder did not produce three pictures, AAC and a valid codec header');
    if (options.realtime) {
      // About one second more of the program through the same encoder, as the live segmenter would feed it.
      const frames = realtimeFrames(options.fps), before = pictures;
      // Synthetic pictures (no program track): two different buffers, built before timing starts.
      const synthetic = reader ? [] : [pixels, pixels.map((v, k) => (k & 3) === 3 ? 255 : (k * 31) & 255)];
      const started = performance.now();
      let maxQueue = 0, lastInputAt = started;
      for (let i = 0; i < frames; i++) {
        if (reader) {
          // A frame read after the attempt already failed is closed here, never leaked.
          const reading = reader.read().then(frame => {
            if (failedSettled || options.signal?.aborted) { frame.value?.close(); throw new Error('preflight ended'); }
            return frame;
          });
          const captured = await Promise.race([reading, failed]);
          if (captured.done || !captured.value) throw new Error('Program video track ended before preflight');
          source = captured.value;
        }
        const timestamp = Math.round((3 + i) * 1e6 / options.fps);
        const frame = source ? new VideoFrame(source, { timestamp }) :
          new VideoFrame(synthetic[i % 2]!, { format: 'RGBA', codedWidth: picture.width, codedHeight: picture.height, timestamp });
        try { video.encode(frame, { keyFrame: false }); } finally { frame.close(); source?.close(); source = undefined; }
        lastInputAt = performance.now();
        maxQueue = Math.max(maxQueue, video.encodeQueueSize ?? 0);
      }
      await Promise.race([video.flush(), failed]);
      options.signal?.throwIfAborted();
      const verdict = { fps: options.fps, frames, outputs: pictures - before, maxQueue,
        tailMs: Math.max(0, lastOutputAt - lastInputAt), elapsedMs: performance.now() - started, paced: Boolean(reader) };
      if (!keptUp(verdict)) {
        throw new Error(`${options.codec.toUpperCase()} encoder cannot keep up with ${options.width}x${options.height}@${options.fps} in real time (queue ${verdict.maxQueue}, tail ${Math.round(verdict.tailMs)} ms)`);
      }
    }
    return {codec:options.codec,codecs:`${actualCodec},mp4a.40.2`,videoConfig:selected.config};
  } finally {
    clearTimeout(stallTimer);
    options.signal?.removeEventListener('abort',onAbort);
    // Browser cleanup can itself stall. Release owned encoders synchronously;
    // never wait for the shared program track's reader to acknowledge cancel.
    try { if(video?.state!=='closed')video?.close(); } catch { /* Already failed. */ }
    try { if(audio?.state!=='closed')audio?.close(); } catch { /* Already failed. */ }
    source?.close();
    try { void reader?.cancel().catch(()=>{}); } catch { /* Already released. */ }
  }
}
